/**
 * StepsManager Component
 *
 * Manages the addition, editing, deletion, and reordering of task steps.
 *
 * Functionality rules for steps:
 * 1. Steps are managed locally within the component state.
 * 2. For new tasks, steps are collected but not saved until the task is created.
 * 3. For existing tasks, steps are fetched when the dialog opens.
 * 4. Steps are only saved to the database when the user submits the form (for both new and existing tasks).
 * 5. There is no auto-save functionality for steps.
 * 6. Users can add, edit, delete, and reorder steps before submitting the form.
 * 7. The checkbox for each step is functional but only affects the local state until form submission.
 * 8. When creating a new task, the steps are created after the task is successfully saved.
 * 9. When updating an existing task, all steps are updated along with the task information.
 * 10. Step position is determined by the order in the local state array.
 *
 * Props:
 * - steps: Array of current steps.
 * - setSteps: Function to update the steps state in the parent component.
 */

import { Button, Checkbox, Input, XStack, YStack } from '@my/ui'
import { Check as CheckIcon, Plus, X } from '@tamagui/lucide-icons'
import { StepFormData } from 'app/data/types/tasks'
import React, { useRef, useState } from 'react'
import { NativeSyntheticEvent, TextInput, TextInputKeyPressEventData } from 'react-native'

interface StepsManagerProps {
  steps: StepFormData[]
  setSteps: React.Dispatch<React.SetStateAction<StepFormData[]>>
}

const StepsManager: React.FC<StepsManagerProps> = ({ steps = [], setSteps }) => {
  const lastInputRef = useRef<TextInput>(null)
  const [draftText, setDraftText] = useState('')
  
  const handleConfirmStep = () => {
    if (!draftText.trim()) return

    const newStep: StepFormData = {
      name: draftText.trim(),
      status: 'incomplete',
      position: steps.length,
      description: '',
    }

    setSteps([...steps.filter(step => step.name.trim() !== ''), newStep])
    setDraftText('')
    
    setTimeout(() => {
      lastInputRef.current?.focus()
    }, 0)
  }

  const handleDeleteStep = (indexToDelete: number) => {
    setSteps(steps.filter((_, index) => index !== indexToDelete))
  }

  const toggleStepCompletion = (index: number) => {
    const updatedSteps = [...steps]
    const step = updatedSteps[index]
    
    // Prevent interaction with deleted steps
    if (step.status === 'deleted') return
    
    updatedSteps[index] = {
      ...updatedSteps[index],
      status: updatedSteps[index].status === 'completed' ? 'incomplete' : 'completed',
    }
    setSteps(updatedSteps)
  }

  return (
    <YStack 
      gap="$1" 
      paddingTop="$3"
    >
      {(steps || []).filter(step => step.name.trim() !== '').map((step, index) => {
        const isDeleted = step.status === 'deleted'
        return (
        <XStack key={step.id || index} gap="$2" alignItems="center" borderTopWidth={1} borderColor="$color5" paddingTop="$1" opacity={step.status === 'completed' || isDeleted ? 0.6 : 1}>
          <Checkbox
            checked={step.status === 'completed'}
            onCheckedChange={() => toggleStepCompletion(index)}
            size="$4"
            borderWidth={2}
            borderRadius={100}
            backgroundColor={step.status === 'completed' ? '#4AC985' : 'transparent'}
            borderColor={step.status === 'completed' ? '#4AC985' : isDeleted ? '$red10Light' : '$color5'}
            disabled={isDeleted}
            hoverStyle={{
              borderColor: '#4AC985',
            }}
            focusStyle={{
              borderColor: '#4AC985',
            }}
            pressStyle={{
              borderColor: '#4AC985',
              backgroundColor: '#4AC985',
              scale: .95,
            }}
          >
            <Checkbox.Indicator>
              <CheckIcon color="$color1" size={15} strokeWidth={4} />
            </Checkbox.Indicator>
          </Checkbox>
          <Input
            paddingLeft="$2"
            flex={1}
            value={step.name}
            onChangeText={(text) => {
              // Prevent editing deleted steps
              if (isDeleted) return
              const updatedSteps = [...steps]
              updatedSteps[index].name = text
              setSteps(updatedSteps)
            }}
            fontSize={16}
            height="auto"
            minHeight={40}
            unstyled  
            textDecorationLine={step.status === 'completed' || isDeleted ? 'line-through' : 'none'}
            color={step.status === 'completed' ? '$color6' : isDeleted ? '$red10Light' : '$color12'}
            editable={!isDeleted}
          />
          <Button
            size="$2"
            circular
            icon={X}
            onPress={() => handleDeleteStep(index)}
            theme="alt1"
            padding={0}
            backgroundColor="transparent"
            hoverStyle={{ backgroundColor: '$color1', borderColor: 'transparent' }}
          />
        </XStack>
        )
      })}

      <XStack gap="$1" alignItems="center" borderTopWidth={1} borderColor="$color5" paddingTop="$1">
        <Plus size={18} color="$color5" strokeWidth={3} marginLeft="$0.5" marginRight="$2"/>
        <Input
          paddingLeft="$2"
          flex={1}
          value={draftText}
          onChangeText={setDraftText}
          placeholder={steps.filter(step => step.name.trim() !== '').length === 0 ? "Add Step" : "Next Step"}
          placeholderTextColor="$color6"
          onKeyPress={(e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
            if (e.nativeEvent.key === 'Enter' && draftText.trim()) {
              e.preventDefault?.()
              handleConfirmStep()
            }
          }}
          onBlur={() => {
            if (draftText.trim()) {
              handleConfirmStep()
            }
          }}
          ref={lastInputRef}
          fontSize={16}
          height="auto"
          minHeight={40}
          unstyled
        />
      </XStack>
    </YStack>
  )
}

export default StepsManager
